import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Mail, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';
import { createTemplate, updateTemplate, DripEmailTemplate } from '@/actions/drip-campaigns';
import api from '@/utils/api/optimized-api';
import TiptapEmailEditor from './TiptapEmailEditor';

interface TemplateSlideFormProps {
  isOpen: boolean;
  onClose: () => void;
  template: DripEmailTemplate | null;
  onSuccess: () => void;
}

interface CrmColumn {
  column_name: string;
  display_name: string;
  data_type: string;
  is_required: boolean;
  category: string;
}

interface FieldMapping {
  leadField: string;
  displayName: string;
  variableName: string;
  default: string;
}

const CATEGORIES = [
  { value: 'welcome', label: 'Welcome' },
  { value: 'nurture', label: 'Nurture' },
  { value: 'educational', label: 'Educational' },
  { value: 're_engagement', label: 'Re-engagement' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'other', label: 'Other' },
];

// Helper to categorize CRM columns
const getCategoryFromColumnName = (columnName: string): string => {
  const contactFields = ['name', 'first_name', 'last_name', 'email', 'phone', 'alternate_phone', 'whatsapp_number'];
  const businessFields = ['company_name', 'job_title', 'industry', 'website'];

  if (contactFields.includes(columnName)) return 'contact';
  if (businessFields.includes(columnName)) return 'business';
  return 'custom';
};

const TemplateSlideForm: React.FC<TemplateSlideFormProps> = ({
  isOpen,
  onClose,
  template,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<{
    template_name: string;
    description: string;
    category: 'welcome' | 'nurture' | 'educational' | 're_engagement' | 'promotional' | 'other';
    subject_line: string;
    preheader_text: string;
    html_body: string;
    plain_text_body: string;
  }>({
    template_name: '',
    description: '',
    category: 'other',
    subject_line: '',
    preheader_text: '',
    html_body: '',
    plain_text_body: '',
  });

  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [crmColumns, setCrmColumns] = useState<CrmColumn[]>([]);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCrmColumns();
      if (template) {
        setFormData({
          template_name: template.template_name,
          description: template.description || '',
          category: template.category,
          subject_line: template.subject_line,
          preheader_text: template.preheader_text || '',
          html_body: template.html_body,
          plain_text_body: template.plain_text_body || '',
        });

        // Convert variables format to field mappings
        const mappings = template.variables.map((v: any) => ({
          leadField: v.leadField || '', // Load saved leadField mapping
          displayName: v.label,
          variableName: v.name,
          default: v.default,
        }));
        setFieldMappings(mappings);
      } else {
        // Reset form for new template
        setFormData({
          template_name: '',
          description: '',
          category: 'other',
          subject_line: '',
          preheader_text: '',
          html_body: '',
          plain_text_body: '',
        });
        setFieldMappings([]);
      }
    }
  }, [isOpen, template]);

  const loadCrmColumns = async () => {
    setIsLoadingColumns(true);
    try {
      const response = await api.req({
        url: '/integrations/crm/lead-columns',
        method: api.Method.GET
      });

      if (response.success && response.data && Array.isArray(response.data)) {
        const mappedColumns: CrmColumn[] = response.data.map((col: any) => ({
          column_name: col.col_name,
          display_name: col.col_label,
          data_type: col.col_type,
          is_required: col.col_required,
          category: getCategoryFromColumnName(col.col_name)
        }));

        setCrmColumns(mappedColumns);
      } else {
        throw new Error(response.message || 'Failed to load CRM columns');
      }
    } catch (error) {
      console.error('Error loading CRM columns:', error);
      toast.error('Failed to load CRM columns');
    } finally {
      setIsLoadingColumns(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddMapping = () => {
    setFieldMappings((prev) => [
      ...prev,
      { leadField: '', displayName: '', variableName: '', default: '' },
    ]);
  };

  const handleMappingChange = (index: number, field: keyof FieldMapping, value: string) => {
    setFieldMappings((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // If lead field is selected, auto-populate display name and variable name
      if (field === 'leadField' && value) {
        const column = crmColumns.find(col => col.column_name === value);
        if (column) {
          updated[index].displayName = column.display_name;
          updated[index].variableName = value; // Use column name as variable name
        }
      }

      return updated;
    });
  };

  const handleRemoveMapping = (index: number) => {
    setFieldMappings((prev) => prev.filter((_, i) => i !== index));
  };

  const getCrmColumnsByCategory = (category: string) => {
    return crmColumns.filter(col => col.category === category);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.template_name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!formData.subject_line.trim()) {
      toast.error('Subject line is required');
      return;
    }
    if (!formData.html_body.trim()) {
      toast.error('HTML body is required');
      return;
    }

    // Validate field mappings
    for (const mapping of fieldMappings) {
      if (!mapping.variableName.trim()) {
        toast.error('All field mappings must have a variable name');
        return;
      }
      if (!mapping.displayName.trim()) {
        toast.error('All field mappings must have a display name');
        return;
      }
    }

    try {
      setSaving(true);

      // Convert field mappings to variables format for backend
      const variables = fieldMappings.map(m => ({
        name: m.variableName,
        label: m.displayName,
        default: m.default,
        required: false,
        leadField: m.leadField, // Store lead field mapping
      }));

      const payload = {
        ...formData,
        variables,
        tags: [], // Remove tags functionality
      };

      if (template) {
        await updateTemplate(template.id, payload);
        toast.success('Template updated successfully');
      } else {
        await createTemplate(payload);
        toast.success('Template created successfully');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SettingsSlideModal
      isOpen={isOpen}
      onClose={onClose}
      title={template ? 'Edit Template' : 'Create New Template'}
      subtitle={template ? 'Update your email template' : 'Create a new email template for your drip campaigns'}
      icon={<Mail className="w-6 h-6" />}
    >
      <>
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={formData.template_name}
                  onChange={(e) => handleInputChange('template_name', e.target.value)}
                  placeholder="e.g., Welcome Email, Follow-up Sequence"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Brief description of this template's purpose"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value as typeof formData.category)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:border-blue-500"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Email Content */}
            <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Email Content</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject Line *
                </label>
                <input
                  type="text"
                  value={formData.subject_line}
                  onChange={(e) => handleInputChange('subject_line', e.target.value)}
                  placeholder="Use {{variable_name}} for dynamic content"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Preheader Text
                </label>
                <input
                  type="text"
                  value={formData.preheader_text}
                  onChange={(e) => handleInputChange('preheader_text', e.target.value)}
                  placeholder="Preview text shown in email clients"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  HTML Body *
                </label>
                <TiptapEmailEditor
                  value={formData.html_body}
                  onChange={(html) => handleInputChange('html_body', html)}
                  placeholder="Enter your email content. Use {{variable_name}} for dynamic fields."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Plain Text Body
                </label>
                <textarea
                  value={formData.plain_text_body}
                  onChange={(e) => handleInputChange('plain_text_body', e.target.value)}
                  placeholder="Plain text version (optional, for email clients that don't support HTML)"
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Field Mappings */}
            <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Field Mappings</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Map lead fields to template variables. Use {`{{variable_name}}`} in your template.
                  </p>
                </div>
                <button
                  onClick={handleAddMapping}
                  type="button"
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                >
                  <FileText className="w-4 h-4" />
                  Add Field
                </button>
              </div>

              {isLoadingColumns ? (
                <div className="flex items-center justify-center h-20">
                  <div className="flex items-center space-x-2">
                    <RefreshCw className="h-5 w-5 animate-spin text-blue-500" />
                    <span className="text-gray-600 dark:text-gray-400">Loading lead fields...</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {fieldMappings.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        No field mappings yet. Add fields to map lead data to your template.
                      </p>
                    </div>
                  ) : (
                    fieldMappings.map((mapping, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800 space-y-3"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* Lead Field Selection */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Lead Field (Optional)
                            </label>
                            <select
                              value={mapping.leadField}
                              onChange={(e) => handleMappingChange(index, 'leadField', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                              <option value="">-- Custom Value --</option>
                              <optgroup label="Contact Fields">
                                {getCrmColumnsByCategory('contact').map((column) => (
                                  <option key={column.column_name} value={column.column_name}>
                                    {column.display_name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Business Fields">
                                {getCrmColumnsByCategory('business').map((column) => (
                                  <option key={column.column_name} value={column.column_name}>
                                    {column.display_name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Custom Fields">
                                {getCrmColumnsByCategory('custom').map((column) => (
                                  <option key={column.column_name} value={column.column_name}>
                                    {column.display_name}
                                  </option>
                                ))}
                              </optgroup>
                            </select>
                          </div>

                          {/* Variable Name */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Variable Name *
                            </label>
                            <input
                              type="text"
                              value={mapping.variableName}
                              onChange={(e) => handleMappingChange(index, 'variableName', e.target.value)}
                              placeholder="e.g., lead_name"
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Use as: {`{{${mapping.variableName || 'variable_name'}}}`}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* Display Name */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Display Name *
                            </label>
                            <input
                              type="text"
                              value={mapping.displayName}
                              onChange={(e) => handleMappingChange(index, 'displayName', e.target.value)}
                              placeholder="e.g., Lead Name"
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>

                          {/* Default Value */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Default Value
                            </label>
                            <input
                              type="text"
                              value={mapping.default}
                              onChange={(e) => handleMappingChange(index, 'default', e.target.value)}
                              placeholder="Fallback if field is empty"
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <button
                            onClick={() => handleRemoveMapping(index)}
                            type="button"
                            className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                          >
                            <span>Remove</span>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div></div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{saving ? 'Saving...' : 'Save Template'}</span>
              </button>
            </div>
          </div>
        </div>
      </>
    </SettingsSlideModal>
  );
};

export default TemplateSlideForm;
